import React from "react";
import {
  User,
  Phone,
  MapPin,
  Map,
  MessageSquare,
  ChevronUp,
  ChevronDown,
  Link as LinkIcon,
  Download,
} from "lucide-react";

const OrderMobileCardList = ({
  orders,
  expandedOrders,
  toggleExpand,
  borderClass = "border-purple-100",
  dateClass = "text-purple-600",
  productCardClass = "bg-teal-50 border-teal-100",
  productTitleClass = "text-teal-700",
  productLinkClass = "text-teal-600 hover:text-teal-800",
  onPhoneClick,
  renderActions,
  renderExpanded,
  onDownload,
  expandLabelCollapsed = "عرض المنتجات والحذف",
  expandLabelExpanded = "إخفاء المنتجات",
}) => {
  const renderPhone = (phone, isSecondary = false) => {
    const sizeClass = isSecondary ? "text-lg" : "text-xl";
    const commonClass =
      "flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-3 py-1 text-green-700 active:bg-green-100 transition w-full text-right";

    if (onPhoneClick) {
      return (
        <button type="button" onClick={() => onPhoneClick(phone)} className={commonClass} title="اضغط لنسخ الرقم">
          <Phone size={20} className="text-green-600 flex-shrink-0" />
          <span className={`${sizeClass} font-bold tracking-wide`} style={{ fontFamily: "monospace" }}>
            {phone}
          </span>
        </button>
      );
    }

    return (
      <a
        href={`https://wa.me/${phone.replace(/[^0-9]/g, "").replace(/^0/, "20")}`}
        target="_blank"
        rel="noopener noreferrer"
        className={commonClass}
      >
        <Phone size={20} className="text-green-600 flex-shrink-0" />
        <span className={`${sizeClass} font-bold tracking-wide`} style={{ fontFamily: "monospace" }}>
          {phone}
        </span>
      </a>
    );
  };

  const isValidSecondPhone = (phone) =>
    phone && phone !== "غير معروف" && phone !== "Unknown Phone";

  return (
    <div className="flex flex-col gap-5 md:hidden">
      {orders.map((order) => {
        const isExpanded = expandedOrders.has(order.orderNumber);
        return (
          <div
            key={order.orderNumber}
            className={`rounded-2xl border ${borderClass} shadow-md overflow-hidden bg-white`}
          >
            {order.note ? (
              <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 flex items-start gap-2">
                <MessageSquare size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <p style={{ fontFamily: "cairo black" }} className="text-[17px] text-amber-800 leading-snug">
                  {order.note}
                </p>
              </div>
            ) : (
              <div className="bg-gray-50 border-b border-gray-100 px-4 py-2 flex items-center gap-2">
                <MessageSquare size={14} className="text-gray-300" />
                <p className="text-sm text-gray-400 italic">لا توجد ملاحظات</p>
              </div>
            )}

            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-mono font-bold text-gray-400 text-lg">#{order.orderNumber}</span>
                <span className={`text-lg ${dateClass}`}>{order.date}</span>
              </div>

              <div className="flex items-center gap-2">
                <User size={15} className="text-gray-500" />
                <span className="font-semibold text-gray-800 text-lg">{order.name}</span>
              </div>

              <div className="space-y-2">
                {renderPhone(order.phoneNumber)}
                {isValidSecondPhone(order.phoneNumber2) && renderPhone(order.phoneNumber2, true)}
              </div>

              <div className="bg-gray-50 rounded-xl p-3 space-y-1">
                <div className="flex items-start gap-2">
                  <MapPin size={15} className="text-gray-500 mt-0.5 flex-shrink-0" />
                  <span style={{ fontFamily: "cairo black" }} className="text-[12px] text-gray-700 leading-snug">
                    {order.address}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Map size={15} className="text-purple-500" />
                  <span className="text-lg font-bold text-purple-600">{order.state}</span>
                </div>
              </div>

              {renderActions?.(order)}

              <button
                type="button"
                onClick={() => toggleExpand(order.orderNumber)}
                className="w-full flex items-center justify-between bg-green-50 rounded-xl px-3 py-2 border border-green-200 active:bg-green-100 transition"
              >
                <div className="flex items-center gap-2">
                  {isExpanded ? (
                    <ChevronUp size={20} className="text-green-600" />
                  ) : (
                    <ChevronDown size={20} className="text-green-600" />
                  )}
                  <span className="text-gray-500 text-sm">
                    {isExpanded ? expandLabelExpanded : expandLabelCollapsed}
                  </span>
                </div>
                <span className="text-xl font-bold text-green-600">{order.total} ج.م</span>
              </button>

              {isExpanded && (
                <div className="space-y-3">
                  <div className="space-y-1">
                    {order.items.map((item, index) => (
                      <div
                        key={`${item.id}-${index}`}
                        className={`${productCardClass} rounded-lg p-1.5 border`}
                      >
                        <div className={`font-medium ${productTitleClass} text-xs leading-tight`}>{item.name}</div>
                        <div style={{ fontFamily: "cairo black" }} className="text-[10px] text-gray-600 leading-tight">
                          {item.type}
                        </div>
                        <div className="flex items-center gap-1 mt-1 flex-wrap">
                          <span
                            className={`text-[10px] px-1.5 py-[2px] rounded font-mono ${
                              item.code?.startsWith("CUSTOM") ? "bg-green-100" : "bg-gray-100"
                            }`}
                          >
                            {item.code}
                          </span>
                          {item.driveLink && (
                            <div className="flex items-center gap-1">
                              <a
                                href={item.driveLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={productLinkClass}
                                title="فتح الرابط"
                              >
                                <LinkIcon size={12} />
                              </a>
                              {item.code?.startsWith("CUSTOM") && onDownload && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    onDownload(item.driveLink, `${order.orderNumber}-${item.id}.jpg`)
                                  }
                                  className="flex items-center gap-1 text-white px-2 py-[2px] rounded text-[10px]"
                                  style={{ backgroundColor: "#15d225" }}
                                >
                                  <Download size={10} />
                                  Download
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {renderExpanded?.(order)}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default OrderMobileCardList;
